import React from 'react';
import { ArrowUpRight, Instagram, HeartHandshake, Sparkles } from 'lucide-react';
import { BKKBUTTER } from '../mock';
import InstagramPreview from './InstagramPreview';

/**
 * BKKButterBento — a full-width editorial bento for BKKButter.
 * 12-column, 3-row grid on desktop; stacks gracefully on mobile.
 *
 * Tiles:
 *   T1 hero image (5x2)      T2 headline (4x1)      T3 IG phone (3x3)
 *                            T4 stat (2) T5 stat(2)
 *   T6 quote (5x1)           T7 flavors (4x1)
 */
export default function BKKButterBento() {
  const b = BKKBUTTER;
  const [s1, s2] = b.stats;

  return (
    <section
      id="bkkbutter"
      className="py-24 md:py-32"
      style={{ background: 'var(--bone)' }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Section header */}
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
          <div className="md:col-span-6">
            <div
              className="mono text-[11px] uppercase tracking-[0.2em] mb-4"
              style={{ color: 'var(--terracotta)' }}
            >
              § 03.5 · A closer look
            </div>
            <h2
              className="display"
              style={{
                fontSize: 'clamp(2rem, 4.5vw, 3.6rem)',
                lineHeight: 1.05,
                fontWeight: 400,
                color: 'var(--ink)',
              }}
            >
              BKKButter,{' '}
              <em
                className="serif"
                style={{ fontStyle: 'italic', color: 'var(--forest)', fontWeight: 400 }}
              >
                a bakery that taught me how people decide.
              </em>
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 flex items-end">
            <p
              className="text-[15px] md:text-[17px] leading-relaxed"
              style={{ color: 'var(--forest-soft)' }}
            >
              At age 14, I started baking Korean macarons to fundraise for Myanmar refugees. I thought Bangkok humidity would be the hardest part, but earning trust online was harder.
            </p>
          </div>
        </div>

        {/* Bento grid */}
        <div
          className="grid grid-cols-12 gap-3 md:gap-4"
          style={{ gridAutoRows: 'minmax(150px, auto)' }}
        >
          {/* T1 — Hero image (tall) */}
          <Tile
            className="col-span-12 md:col-span-5 md:row-span-2"
            padded={false}
            aspect="4/5"
          >
            <img
              src={b.heroImage}
              alt="BKKButter signature holiday macaron box"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(180deg, rgba(15,27,20,0) 55%, rgba(15,27,20,0.5) 100%)',
              }}
            />
            <div
              className="absolute top-4 left-4 inline-flex items-center gap-2 mono text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full"
              style={{ background: 'var(--cream)', color: 'var(--forest)' }}
            >
              <span
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{ background: 'var(--terracotta)' }}
              />
              {b.kicker}
            </div>
            <a
              href={b.link}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open @bkkbutter on Instagram"
              className="absolute bottom-4 right-4 w-10 h-10 rounded-full flex items-center justify-center"
              style={{
                background: 'var(--cream)',
                boxShadow: '0 10px 30px -10px rgba(15,27,20,0.35)',
              }}
            >
              <ArrowUpRight size={16} style={{ color: 'var(--forest)' }} />
            </a>
          </Tile>

          {/* T2 — Headline */}
          <Tile
            className="col-span-12 md:col-span-4 md:row-span-1 flex flex-col justify-between gap-5"
            bg="cream"
          >
            <div
              className="mono text-[10px] uppercase tracking-[0.22em]"
              style={{ color: 'var(--terracotta)' }}
            >
              the idea
            </div>
            <h3
              className="display"
              style={{
                fontSize: 'clamp(1.4rem, 2.4vw, 2rem)',
                lineHeight: 1.1,
                fontWeight: 500,
                color: 'var(--ink)',
                letterSpacing: '-0.015em',
              }}
            >
              People do not just buy food.{' '}
              <em
                className="serif"
                style={{ fontStyle: 'italic', color: 'var(--forest)', fontWeight: 500 }}
              >
                They buy how it makes them feel.
              </em>
            </h3>
            <p
              className="text-[13px] leading-relaxed"
              style={{ color: 'var(--forest-soft)' }}
            >
              {b.sub}
            </p>
          </Tile>

          {/* T3 — IG phone (tall, spans 3 rows) */}
          <Tile
            className="col-span-12 md:col-span-3 md:row-span-3"
            bg="boneDeep"
            padded={false}
          >
            <div className="absolute inset-0 p-4 flex items-center justify-center">
              <InstagramPreview ig={b.instagram} />
            </div>
          </Tile>

          {/* T4 — Stat (forest) */}
          <StatTile
            label={s1.label}
            value={s1.value}
            unit={s1.unit}
            bg="var(--forest)"
            valueColor="var(--cream)"
            labelColor="#d4b896"
            unitColor="#d4cbbc"
            icon={<Sparkles size={13} strokeWidth={1.75} />}
          />

          {/* T5 — Stat (terracotta) */}
          <StatTile
            label={s2.label}
            value={s2.value}
            unit={s2.unit}
            bg="var(--terracotta)"
            valueColor="var(--cream)"
            labelColor="rgba(250,246,238,0.7)"
            unitColor="rgba(250,246,238,0.85)"
            icon={<HeartHandshake size={13} strokeWidth={1.75} />}
          />

          {/* T6 — Quote */}
          <Tile
            className="col-span-12 md:col-span-5 flex flex-col justify-between gap-6"
            bg="boneSoft"
          >
            <div
              className="mono text-[10px] uppercase tracking-[0.22em]"
              style={{ color: 'var(--moss)' }}
            >
              a note from the founder
            </div>
            <blockquote
              className="serif"
              style={{
                fontSize: 'clamp(1.05rem, 1.5vw, 1.3rem)',
                lineHeight: 1.45,
                fontStyle: 'italic',
                color: 'var(--ink)',
                fontWeight: 400,
                letterSpacing: '-0.005em',
              }}
            >
              “{b.quote.body}”
            </blockquote>
            <div
              className="mono text-[10px] uppercase tracking-[0.22em]"
              style={{ color: 'var(--stone)' }}
            >
              {b.quote.attribution}
            </div>
          </Tile>

          {/* T7 — Flavors */}
          <Tile
            className="col-span-12 md:col-span-4 flex flex-col gap-4"
            bg="cream"
          >
            <div className="flex items-center justify-between">
              <div
                className="mono text-[10px] uppercase tracking-[0.22em]"
                style={{ color: 'var(--terracotta)' }}
              >
                products
              </div>
              <div
                className="mono text-[10px] tracking-wide"
                style={{ color: 'var(--stone)' }}
              >
                what we sell
              </div>
            </div>
            <ul className="grid grid-cols-1 gap-y-2.5">
              {b.flavors.map((f) => (
                <li key={f.name} className="flex items-center gap-2.5">
                  <span
                    className="inline-block rounded-full shrink-0"
                    style={{
                      width: 10,
                      height: 10,
                      background: f.color,
                      boxShadow: `0 0 0 3px ${f.color}18`,
                    }}
                    aria-hidden
                  />
                  <span
                    className="text-[13px]"
                    style={{ color: 'var(--ink)', letterSpacing: '-0.005em' }}
                  >
                    {f.name}
                  </span>
                </li>
              ))}
            </ul>
          </Tile>
        </div>

        {/* Footnote strip */}
        <div className="mt-8 md:mt-10 flex flex-wrap items-center justify-between gap-4 pt-6 border-t" style={{ borderColor: 'rgba(15,27,20,0.08)' }}>
          <div
            className="mono text-[11px] uppercase tracking-[0.2em] flex items-center gap-3 flex-wrap"
            style={{ color: 'var(--moss)' }}
          >
            <span>2M+ organic reach</span>
            <span style={{ color: 'var(--stone)' }}>·</span>
            <span style={{ color: 'var(--stone)' }}>$10,000+ raised for Myanmar refugees</span>
            <span style={{ color: 'var(--stone)' }}>·</span>
            <span style={{ color: 'var(--stone)' }}>{b.pressNote}</span>
          </div>
          <a
            href={b.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mono text-[11px] uppercase tracking-[0.18em] link-line"
            style={{ color: 'var(--forest)' }}
          >
            <Instagram size={13} strokeWidth={1.75} /> @bkkbutter
            <ArrowUpRight size={13} strokeWidth={1.75} />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ----- helpers ----- */

function Tile({ children, className = '', bg = 'bone', padded = true, aspect }) {
  const bgMap = {
    bone: 'var(--bone)',
    boneSoft: 'var(--bone-soft)',
    boneDeep: 'var(--bone-deep)',
    cream: 'var(--cream)',
  };
  return (
    <div
      className={`relative overflow-hidden rounded-2xl ${
        padded ? 'p-5 md:p-6' : ''
      } ${className}`}
      style={{
        background: bgMap[bg] || bg,
        border: '1px solid rgba(15,27,20,0.06)',
        boxShadow: '0 1px 0 rgba(15,27,20,0.02)',
        aspectRatio: aspect,
      }}
    >
      {children}
    </div>
  );
}

function StatTile({ label, value, unit, bg, valueColor, labelColor, unitColor, icon }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl p-5 md:p-6 col-span-6 md:col-span-2 flex flex-col justify-between gap-4"
      style={{
        background: bg,
        border: '1px solid rgba(15,27,20,0.06)',
        boxShadow: '0 1px 0 rgba(15,27,20,0.02)',
      }}
    >
      <div
        className="mono text-[10px] uppercase tracking-[0.22em] flex items-center gap-1.5"
        style={{ color: labelColor }}
      >
        {icon}
        {label}
      </div>
      <div
        className="display"
        style={{
          fontSize: 'clamp(2.2rem, 3.2vw, 3rem)',
          lineHeight: 0.95,
          fontWeight: 400,
          color: valueColor,
          letterSpacing: '-0.025em',
        }}
      >
        {value}
      </div>
      <div
        className="text-[12px] leading-snug"
        style={{ color: unitColor }}
      >
        {unit}
      </div>
    </div>
  );
}
